'use client'

import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import { toast } from 'react-toastify'
import { SocketContext } from '@/lib/providers/socket-provider'
import { usePermission } from '@/lib/hooks/use-permission'
import { SIDEBAR_MODULE_CONFIG } from '@/layouts/admin/sidebar-config'
import {
    getNotifications,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    Notification,
    NotificationQuery,
} from '@/lib/api/notifications'

// Preferencia (por dispositivo) para desativar o toast flutuante de novas
// notificacoes. O icone de sino continua funcionando normalmente.
export const FLOATING_NOTIFICATIONS_KEY = 'floatingNotificationsDisabled'

export function isFloatingNotificationsDisabled(): boolean {
    try {
        return localStorage.getItem(FLOATING_NOTIFICATIONS_KEY) === 'true'
    } catch {
        return false
    }
}

export function setFloatingNotificationsDisabled(disabled: boolean): void {
    try {
        localStorage.setItem(FLOATING_NOTIFICATIONS_KEY, disabled ? 'true' : 'false')
    } catch {
        // ignore (modo privado/sem storage)
    }
}

// Mapa do nosso NotificationType (enum do backend) para o tipo do toastify.
// SUCCESS/WARNING/ERROR fazem match direto; INFO/SYSTEM/ANNOUNCEMENT caem em info.
function notificationToastType(t: string | undefined): 'success' | 'warning' | 'error' | 'info' {
    if (t === 'SUCCESS') return 'success'
    if (t === 'WARNING') return 'warning'
    if (t === 'ERROR') return 'error'
    return 'info'
}

// Extrai a key do modulo do link da notificacao (/financial/billings -> financial).
// Converte hifen->underscore para casar com as keys do sistema (health_integrations).
function moduleFromLink(link?: string): string | null {
    if (!link) return null
    const match = /^\/([a-z0-9-]+)/i.exec(link.trim())
    if (!match) return null
    return match[1].toLowerCase().replace(/-/g, '_')
}

export function useNotifications() {
    const { socket, isConnected } = useContext(SocketContext)
    const { canAccessModule } = usePermission()
    // Ref mantem o canAccessModule atual sem re-inscrever o socket a cada render.
    const canAccessModuleRef = useRef(canAccessModule)
    useEffect(() => {
        canAccessModuleRef.current = canAccessModule
    })
    const [notifications, setNotifications] = useState<Notification[]>([])
    const [unreadCount, setUnreadCount] = useState(0)
    const [loading, setLoading] = useState(true)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)

    const loadNotifications = useCallback(async (query?: NotificationQuery) => {
        setLoading(true)
        try {
            const response = await getNotifications(query)
            if (response.success) {
                setNotifications(response.result.notifications)
                setUnreadCount(response.result.unreadCount)
                setTotal(response.result.total)
                setPage(response.result.page)
                setTotalPages(response.result.totalPages)
            }
        } finally {
            setLoading(false)
        }
    }, [])

    const loadUnreadCount = useCallback(async () => {
        const response = await getUnreadCount()
        if (response.success) {
            setUnreadCount(response.result.count)
        }
    }, [])

    const handleMarkAsRead = useCallback(async (notificationId: number) => {
        const response = await markAsRead(notificationId)
        if (response.success) {
            setNotifications(prev =>
                prev.map(n =>
                    n.id === notificationId
                        ? { ...n, isRead: true, readAt: new Date().toISOString() }
                        : n
                )
            )
            setUnreadCount(prev => Math.max(0, prev - 1))
        }
        return response
    }, [])

    const handleMarkAllAsRead = useCallback(async () => {
        const response = await markAllAsRead()
        if (response.success) {
            setNotifications(prev =>
                prev.map(n => ({
                    ...n,
                    isRead: true,
                    readAt: new Date().toISOString(),
                }))
            )
            setUnreadCount(0)
        }
        return response
    }, [])

    const handleDelete = useCallback(
        async (notificationId: number) => {
            const response = await deleteNotification(notificationId)
            if (response.success) {
                const deletedNotification = notifications.find(
                    n => n.id === notificationId
                )
                setNotifications(prev =>
                    prev.filter(n => n.id !== notificationId)
                )
                setTotal(prev => prev - 1)
                if (deletedNotification && !deletedNotification.isRead) {
                    setUnreadCount(prev => Math.max(0, prev - 1))
                }
            }
            return response
        },
        [notifications]
    )

    // Carregar notificacoes iniciais
    useEffect(() => {
        loadNotifications()
    }, [loadNotifications])

    // Escutar eventos de WebSocket
    useEffect(() => {
        if (!socket || !isConnected) return

        const handleNewNotification = (notification: Notification) => {
            // Descarta em tempo-real notificacoes de modulos que o usuario nao
            // acessa (o backend ja filtra a listagem; aqui evitamos o vazamento
            // via socket). So bloqueia modulos conhecidos do sistema; links
            // genericos (ex: /account) passam.
            const mod = moduleFromLink(notification.link)
            if (
                mod &&
                SIDEBAR_MODULE_CONFIG[mod] &&
                !canAccessModuleRef.current(mod)
            ) {
                return
            }

            setNotifications(prev => [notification, ...prev])
            setUnreadCount(prev => prev + 1)
            setTotal(prev => prev + 1)

            // Toast flutuante no canto inferior esquerdo com o titulo da
            // notificacao. Curto (3s) pra nao bloquear o painel.
            // Pode ser desativado pelo usuario na aba Notificacoes (por dispositivo).
            if (!isFloatingNotificationsDisabled()) {
                toast(notification.title, {
                    type: notificationToastType(notification.type as string),
                    position: 'bottom-left',
                    autoClose: 3000,
                    hideProgressBar: true,
                    pauseOnHover: true,
                    closeOnClick: true,
                })
            }
        }

        const handleUnreadCountUpdate = (data: { count: number }) => {
            setUnreadCount(data.count)
        }

        socket.on('notification:new', handleNewNotification)
        socket.on('notification:unread-count', handleUnreadCountUpdate)

        return () => {
            socket.off('notification:new', handleNewNotification)
            socket.off('notification:unread-count', handleUnreadCountUpdate)
        }
    }, [socket, isConnected])

    return {
        notifications,
        unreadCount,
        loading,
        total,
        page,
        totalPages,
        loadNotifications,
        loadUnreadCount,
        markAsRead: handleMarkAsRead,
        markAllAsRead: handleMarkAllAsRead,
        deleteNotification: handleDelete,
    }
}
